import React, { useEffect } from 'react';
import styled from 'styled-components';
import { colorsV2 } from '../colors-v2';
import { shaking } from '@doltech/utils/lib/animations/shaking';
import { IconSystemV2LogoMessengerColor2025 } from '@doltech/icons/SystemV2/IconSystemV2LogoMessengerColor2025';
import { IconSystemV2ArrowArrowUpLine } from '@doltech/icons/SystemV2/IconSystemV2ArrowArrowUpLine';
import { motion, AnimatePresence } from 'framer-motion';
import { useMountedState, useWindowScroll } from 'react-use';
import { Typography } from '../DesignSystemV2/Typography';

const ZALO_CHAT_WIDGET_WIDTH = 350;
const ZALO_CHAT_WIDGET_HEIGHT = 200;

const Container = styled.div`
  position: fixed;
  bottom: 60px;
  right: 35px;
  z-index: 99999;

  display: grid;
  justify-items: end;
  gap: 8px;
  ${shaking}

  .zalo-chat-widget {
    bottom: 0 !important;
    right: 0 !important;
    top: auto !important;
    left: auto !important;
  }
`;

const InboxWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  border-radius: 40px;
  background-color: ${colorsV2.white100};
  box-shadow: 0px 10px 20px 0px #3040780f, 0px 2px 6px 0px #3042780f;
  border: none;
  outline: 0;
  cursor: pointer;

  &.arrow {
    font-size: 28px;
  }

  &.messenger {
    font-size: 36px;
  }
`;

const ArrowUpWrapper = styled(motion.div)`
  position: absolute;
  bottom: 125px;
  right: 0;
`;

const TooltipContent = styled.div`
  background-color: ${colorsV2.white100};
  border-radius: 12px;
  border: 1px solid ${colorsV2.neutral40};
  padding: 10px 12px;
  box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  user-select: none;
  z-index: 0;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease-in-out;

  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background-color: ${colorsV2.white100};
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
    border-color: ${colorsV2.neutral40} ${colorsV2.neutral40} transparent transparent;
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  &:hover {
    ${TooltipContent} {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const TooltipWrapperZalo = styled(TooltipWrapper)`
  transform: translate(5px, 5px);
  width: 60px;
  height: 60px;
  position: relative;
  margin-top: -5px;
`;

export const ContactDol = () => {
  const { y } = useWindowScroll();
  const isMounted = useMountedState();
  const mounted = isMounted();

  useEffect(() => {
    if (mounted) {
      if (window.ZaloSocialSDK) {
        window.ZaloSocialSDK.reload();
      } else {
        const script = document.createElement('script');
        script.src = 'https://sp.zalo.me/plugins/sdk.js';
        script.async = true;
        script.onload = () => {
          window.ZaloSocialSDK.init();
        };
        document.body.appendChild(script);
      }

      window.addEventListener('message', (event) => {
        if (event.data?.cmd === 'zchat_widget_toggle_sticker') {
          if (
            event.data?.width === ZALO_CHAT_WIDGET_WIDTH &&
            event.data?.height === ZALO_CHAT_WIDGET_HEIGHT
          ) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'zalo-inbox',
            });
          }
        }
      });
    }
  }, [mounted]);

  function scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  return (
    <Container>
      <AnimatePresence>
        {mounted && y > window.innerHeight / 2 && (
          <ArrowUpWrapper
            key="arrow-up"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TooltipWrapper>
              <InboxWrapper className="arrow" as="button" onClick={scrollToTop}>
                <IconSystemV2ArrowArrowUpLine />
              </InboxWrapper>
              <TooltipContent>
                <Typography.Text variant="regular/14-20" color={colorsV2.neutral180}>
                  Lên đầu trang
                </Typography.Text>
              </TooltipContent>
            </TooltipWrapper>
          </ArrowUpWrapper>
        )}
      </AnimatePresence>

      <TooltipWrapper>
        <a href="https://m.me/dolenglish.ieltsdinhluc" target="_blank" rel="noopener noreferrer nofollow">
          <InboxWrapper className="messenger">
            <IconSystemV2LogoMessengerColor2025 className="shaking" />
          </InboxWrapper>
        </a>
        <TooltipContent>
          <Typography.Text variant="regular/14-20" color={colorsV2.neutral180}>
            Nhắn DOL qua Facebook
          </Typography.Text>
        </TooltipContent>
      </TooltipWrapper>
      <TooltipWrapperZalo>
        <div
          tabIndex={1}
          className="zalo-chat-widget"
          data-oaid="3524453634164038405"
          data-welcome-message="Rất vui khi được hỗ trợ bạn!"
          data-autopopup="0"
          data-width={`${ZALO_CHAT_WIDGET_WIDTH}`}
          data-height={`${ZALO_CHAT_WIDGET_HEIGHT}`}
          data-left-side="false"
        />
        <TooltipContent>
          <Typography.Text variant="regular/14-20" color={colorsV2.neutral180}>
            Nhắn DOL qua Zalo
          </Typography.Text>
        </TooltipContent>
      </TooltipWrapperZalo>
    </Container>
  );
};
